/**
 * Practice Compartment Management for Binary Resources
 *
 * Binary resources don't support standard `_compartment` search in FHIR/Medplum,
 * making them difficult to query and clean up. This module ensures Binary resources
 * have `meta.accounts` set so they can be searched via `_account` parameter.
 *
 * Key utilities:
 * - `ensureBinaryHasPracticeCompartment()` - Sets meta.accounts on existing Binary
 * - `createBinaryWithCompartment()` - Creates Binary with compartment in one call
 * - `getPracticeRefForPatient()` - Extracts practice reference from Patient
 *
 * Usage in models after creating Binary:
 * ```typescript
 * const binary = await medplum.createBinary({ data, contentType, filename });
 * if (binary.id) {
 *   const practiceRef = await getPracticeRefForPatient(medplum, patientId);
 *   if (practiceRef) {
 *     await ensureBinaryHasPracticeCompartment({ medplum, binaryId: binary.id, practiceRef });
 *   }
 * }
 * ```
 *
 * This enables:
 * - Searching Binary via `_account` parameter
 * - Proper cleanup in wipe-practice-data script
 * - Consistent practice isolation across all resource types
 *
 * Note: `meta.accounts` (plural, array) is Medplum-specific and preferred over
 * legacy `meta.account` (singular). Not part of core FHIR R4.
 */

import type { MedplumClient } from "@medplum/core";
import type { Binary, Reference, Resource, ResourceType } from "@medplum/fhirtypes";
import { createLogger } from "@/lib/logger";
import { getPracticeOrganizationFromMeta } from "@/lib/medplum/compartments";
import { retryMedplumCall } from "@/lib/medplum/retry";

const logger = createLogger("lib/fhir/compartment");

/**
 * A client-uploaded Binary must have been created within this window to be eligible for a
 * backend-client compartment tag. It bounds how long a guessed or enumerated Binary id stays usable.
 */
export const MAX_TAGGABLE_BINARY_AGE_MS = 10 * 60 * 1000;

/**
 * Ensures a resource has the practice compartment (meta.accounts) set.
 *
 * Bypasses FhirResource.patchResource protections - use only for migrations/setup.
 * Uses 3 strategies: patch append → patch init → read + update.
 */
export async function ensureResourceHasPracticeCompartment({
  medplum,
  resourceType,
  resourceId,
  practiceRef,
}: {
  medplum: MedplumClient;
  resourceType: ResourceType;
  resourceId: string;
  practiceRef: string | Reference;
}): Promise<void> {
  const reference = typeof practiceRef === "string" ? practiceRef : practiceRef.reference;
  if (!reference) {
    throw new Error("Practice reference is required");
  }

  // Fast path: patch without a read (1 request)
  try {
    await retryMedplumCall(() =>
      medplum.patchResource(resourceType, resourceId, [
        {
          op: "add",
          path: "/meta/accounts/-",
          value: { reference },
        },
      ]),
    );
    return;
  } catch {
    // Ignore and fall through to safer strategy
  }

  // Fallback: initialize meta.accounts when it doesn't exist
  try {
    await retryMedplumCall(() =>
      medplum.patchResource(resourceType, resourceId, [
        {
          op: "add",
          path: "/meta/accounts",
          value: [{ reference }],
        },
      ]),
    );
    return;
  } catch {
    // Ignore and fall through to slowest strategy
  }

  // Last resort: read + update (2 requests, but most compatible)
  const resource = await retryMedplumCall(() => medplum.readResource(resourceType, resourceId));

  // meta.accounts is a Medplum extension (not in core FHIR types)
  const existingAccounts = ((resource.meta as any)?.accounts as Reference[] | undefined) ?? [];
  const hasAccount = existingAccounts.some((acc: Reference) => acc.reference === reference);

  if (!hasAccount) {
    await retryMedplumCall(() =>
      medplum.updateResource({
        ...resource,
        meta: {
          ...resource.meta,
          accounts: [...existingAccounts, { reference }] as any,
        },
      } as Resource),
    );
  }
}

/**
 * Ensures a Binary resource has the practice compartment set.
 * Wrapper around ensureResourceHasPracticeCompartment for Binary-specific usage.
 */
export async function ensureBinaryHasPracticeCompartment({
  medplum,
  binaryId,
  practiceRef,
}: {
  medplum: MedplumClient;
  binaryId: string;
  practiceRef: string | Reference;
}): Promise<void> {
  return ensureResourceHasPracticeCompartment({
    medplum,
    resourceType: "Binary",
    resourceId: binaryId,
    practiceRef,
  });
}

/**
 * Creates a Binary and sets compartment in one call.
 *
 * Access control: `securityContext` is the *only* mechanism that enforces per-patient
 * /per-practice access on FHIR Binary resources — `meta.accounts` and compartment
 * criteria do NOT apply to Binary. `securityContext` is required so every caller
 * consciously decides what protects the Binary (typically a Patient reference; an
 * Organization reference is acceptable when no patient is in scope, e.g. outbound fax).
 *
 * Failure handling: if compartment tagging fails after Binary creation, the Binary is
 * deleted to avoid leaking an orphaned project-wide PHI blob.
 */
export async function createBinaryWithCompartment({
  medplum,
  data,
  contentType,
  filename,
  practiceRef,
  securityContext,
}: {
  medplum: MedplumClient;
  data: string | Uint8Array | File;
  contentType: string;
  filename?: string;
  practiceRef: string | Reference;
  securityContext: Reference;
}): Promise<Binary & { id: string }> {
  const binary = await medplum.createBinary({
    data,
    contentType,
    filename,
    securityContext,
  });

  if (!binary.id) {
    throw new Error("Created Binary is missing id");
  }

  try {
    await ensureBinaryHasPracticeCompartment({
      medplum,
      binaryId: binary.id,
      practiceRef,
    });
  } catch (error) {
    // Compartment tagging failed - delete the newly created Binary to avoid leaving
    // an orphaned, project-wide-readable PHI blob in Medplum.
    const binaryId = binary.id;
    try {
      await medplum.deleteResource("Binary", binaryId);
    } catch (cleanupError) {
      logger.error("Failed to delete Binary after compartment tagging failure", {
        binaryId,
        cleanupError,
      });
    }
    throw error;
  }

  return binary as Binary & { id: string };
}

/**
 * Extracts practice reference from Patient's meta.accounts.
 * Returns first Organization reference or undefined.
 * Throws InvalidPracticeCompartmentError if multiple Organization references exist.
 */
export function getPracticeRefFromPatient(patient: Resource): string | undefined {
  return getPracticeOrganizationFromMeta(patient.meta)?.reference;
}

/**
 * Fetches Patient and extracts practice reference.
 * Convenience wrapper around readResource + getPracticeRefFromPatient.
 */
export async function getPracticeRefForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<string | undefined> {
  const patient = await medplum.readResource("Patient", patientId);
  return getPracticeRefFromPatient(patient);
}
